/**
 * The operator session.
 *
 * Deliberately its own provider, its own token and its own storage key,
 * sitting beside the club session rather than above it. The two are
 * different principals — one is inside a club, one is outside every club
 * — and a single provider that tried to be either would eventually be
 * asked which one it was in a place where the answer mattered.
 *
 * Holding both at once is the normal case, not an edge one: an operator
 * opens a club, works inside it, and comes back. So the operator token
 * survives that round trip, and the club token it hands out is stored
 * where the ordinary session already looks for one.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, setToken } from './api.ts';

const OPERATOR_TOKEN_KEY = 'betterclub.operator_token';

/** Set while an operator is inside a club, so the club app can say so. */
const ACTING_KEY = 'betterclub.acting_as';

export interface Operator {
  id: string;
  email: string;
  display_name: string;
  must_change_password: boolean;
  last_login_at: string | null;
}

export interface ActingAs {
  club_slug: string;
  club_name: string;
  operator_email: string;
  until: number;
}

export function getOperatorToken(): string | null {
  return localStorage.getItem(OPERATOR_TOKEN_KEY);
}

function setOperatorToken(token: string | null): void {
  if (token) localStorage.setItem(OPERATOR_TOKEN_KEY, token);
  else localStorage.removeItem(OPERATOR_TOKEN_KEY);
}

export function getActingAs(): ActingAs | null {
  try {
    const raw = localStorage.getItem(ACTING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ActingAs;
    // A support token lasts fifteen minutes. Past that the banner would be
    // claiming something the API has already stopped believing.
    if (parsed.until < Date.now()) {
      localStorage.removeItem(ACTING_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearActingAs(): void {
  localStorage.removeItem(ACTING_KEY);
}

/**
 * The operator token stopped being accepted.
 *
 * The same defect the club session had, in the same shape: clearing the
 * stored token told nothing in React, so the console went on rendering a
 * signed-in operator over a screen where every call failed. Its own
 * listener set rather than the club one's — these are two different
 * principals, and an expired club token must not sign an operator out of
 * the console they are watching it from.
 */
type OperatorUnauthorizedListener = () => void;
const operatorUnauthorizedListeners = new Set<OperatorUnauthorizedListener>();
let operatorAnnounced = false;

function onOperatorUnauthorized(fn: OperatorUnauthorizedListener): () => void {
  operatorUnauthorizedListeners.add(fn);
  return () => operatorUnauthorizedListeners.delete(fn);
}

function announceOperatorUnauthorized(): void {
  if (operatorAnnounced) return;
  operatorAnnounced = true;
  for (const fn of operatorUnauthorizedListeners) {
    try {
      fn();
    } catch {
      /* one listener must not stop the others */
    }
  }
}

/**
 * Call the platform API.
 *
 * Its own client rather than the shared `api()`, because that one attaches
 * the club token — which an operator route refuses, correctly, and which
 * would be a confusing way to be told you are signed out.
 */
export async function platformApi<T = unknown>(
  path: string,
  options: { method?: string; body?: unknown } = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getOperatorToken();
  if (token) headers.authorization = `Bearer ${token}`;
  if (options.body !== undefined) headers['content-type'] = 'application/json';

  const res = await fetch(path, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  const text = await res.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }

  if (!res.ok) {
    const envelope = payload as { error?: { code: string; message: string; detail?: unknown } };
    if (res.status === 401) {
      setOperatorToken(null);
      announceOperatorUnauthorized();
    }
    throw new ApiError(
      res.status,
      envelope?.error?.code ?? 'unknown',
      envelope?.error?.message ?? `Request failed (${res.status})`,
      envelope?.error?.detail,
    );
  }

  return payload as T;
}

interface OperatorSessionValue {
  loading: boolean;
  operator: Operator | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  refresh: () => Promise<void>;
  /** Take a club token and go and use it. */
  openClub: (clubId: string, reason: string) => Promise<string>;
}

const OperatorContext = createContext<OperatorSessionValue | null>(null);

export function OperatorProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [operator, setOperator] = useState<Operator | null>(null);

  const load = useCallback(async () => {
    if (!getOperatorToken()) {
      setOperator(null);
      setLoading(false);
      return;
    }
    try {
      const me = await platformApi<{ operator: Operator }>('/v1/platform/me');
      setOperator(me.operator);
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 401)) console.error(e);
      setOperatorToken(null);
      setOperator(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Same fix as the club session: the expiry puts the sign-in card up. */
  useEffect(
    () =>
      onOperatorUnauthorized(() => {
        setOperator(null);
        setLoading(false);
      }),
    [],
  );

  const value = useMemo<OperatorSessionValue>(
    () => ({
      loading,
      operator,
      async signIn(email, password) {
        const res = await platformApi<{ token: string }>('/v1/platform/auth/login', {
          method: 'POST',
          body: { email, password },
        });
        setOperatorToken(res.token);
        operatorAnnounced = false;

        // The login response carries an operator object too, but a SMALLER
        // one: `must_change_password` sits beside it on the envelope rather
        // than inside it. Storing that object gave the session an operator
        // whose flag was undefined — false, in effect, at the one moment it
        // decides anything. `/me` is the only shape with every field, so the
        // session is built from it and from nothing else, and a field added
        // there later cannot go missing here.
        await load();
      },
      signOut() {
        setOperatorToken(null);
        operatorAnnounced = false;
        setOperator(null);
      },
      refresh: load,
      async openClub(clubId, reason) {
        const res = await platformApi<{
          token: string;
          expires_in: number;
          club: { slug: string; legal_name: string };
          acting_operator: { email: string };
        }>(`/v1/platform/clubs/${clubId}/open`, { method: 'POST', body: { reason } });

        // The club token goes where the ordinary session looks for one, so
        // the rest of the app needs to know nothing about any of this. The
        // operator token stays put: coming back is the normal ending.
        setToken(res.token);

        localStorage.setItem(
          ACTING_KEY,
          JSON.stringify({
            club_slug: res.club.slug,
            club_name: res.club.legal_name,
            operator_email: res.acting_operator.email,
            until: Date.now() + res.expires_in * 1000,
          } satisfies ActingAs),
        );

        return res.club.legal_name;
      },
    }),
    [loading, operator, load],
  );

  return <OperatorContext.Provider value={value}>{children}</OperatorContext.Provider>;
}

export function useOperator(): OperatorSessionValue {
  const ctx = useContext(OperatorContext);
  if (!ctx) throw new Error('useOperator must be used inside OperatorProvider');
  return ctx;
}
